/**
 * @jsxFrag
 * @jsxRuntime classic
 * @jsx jsx
 */
import { ReactNode } from 'react';

import { cssMap, jsx } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { ExpandableMenuItemLevelContext } from '../expandable-menu-item/expandable-menu-item-level-context';
import { MenuItemBase } from '../menu-item';
import { COLLAPSE_ELEM_BEFORE } from '../menu-item-signals';

const dragPreviewStyles = cssMap({
	root: {
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderStyle: 'solid',
		backgroundColor: token('elevation.surface'),
		// menu items already have a border radius, but adding on this element too
		// as we are adding a border on this element
		borderRadius: token('border.radius'),

		// helps the preview feel more feel more balanced
		paddingInlineEnd: token('space.050'),
		// cannot go above 280px (web platform limitation), so leaving a bit of room
		maxWidth: 260,
	},
});

/**
 * A drag preview for sidebar menu items.
 *
 * The limited API corresponds with the limited amount of information we
 * want to show in drag previews.
 *
 * If no `elemBefore` is provided, then the `elemBefore` will automatically collapse.
 * There is no need to pass in `COLLAPSE_ELEM_BEFORE`. We do this as there is no
 * need to maintain vertical side navigation vertical alignment in the drag preview.
 *
 * Please ensure the drag preview is pushed away from the users pointer
 * (with `pointerOutsideOfPreview()` from Pragmatic drag and drop) by our
 * standard amount (`{x: token('space.200'), y: token('space.100')`)
 */
export function DragPreview({
	elemBefore,
	children,
}: {
	elemBefore?: ReactNode;
	children: ReactNode;
}) {
	return (
		// Resetting the expandable menu items to 0.
		// This is to prevent our hidden element from pushing
		// the drag preview further away from the users pointer
		<ExpandableMenuItemLevelContext.Provider value={0}>
			<div css={dragPreviewStyles.root}>
				{/* For drag previews, we can collapse if there is no elemBefore as we don't
			need to worry about vertical alignment with other elements */}
				<MenuItemBase elemBefore={elemBefore ?? COLLAPSE_ELEM_BEFORE}>{children}</MenuItemBase>
			</div>
		</ExpandableMenuItemLevelContext.Provider>
	);
}
